import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { User } from "../../models/userModels";
import TraineeService from "../services/TraineeService";
import { setError } from "./errorSlice";
import { error500Text } from "../../util/error-helper";

export interface GroupedUser {
  group_name: string;
  users: User[];
}

interface TraineesState {
  loading: boolean;
  error: undefined | string;
  groupedAndSortedAdditionalTrainees: GroupedUser[];
  groupedAndSortedMasterTrainees: GroupedUser[];
  traineesWithOpenEntries: User[];
}

const initialState: TraineesState = {
  groupedAndSortedAdditionalTrainees: [],
  groupedAndSortedMasterTrainees: [],
  loading: false,
  error: undefined,
  traineesWithOpenEntries: [],
};

export const getGroupedAndSortedAdditionalTrainees = createAsyncThunk(
  "trainees/getGroupedAndSortedAdditionalTrainees",
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const res = await TraineeService.getGroupedAndSortedTrainees(
        data.group,
        data.sort,
        0
      );
      if (res.error) {
        dispatch(setError({
          isOpen: true,
          message: res.error
        }))
        return rejectWithValue(res.error);
      } else if (res.users) {
        return res.users;
      } else {
        dispatch(setError({
          isOpen: true,
          message: error500Text
        }))
        return rejectWithValue(error500Text);
      }
    } catch (err) {
      dispatch(setError({
        isOpen: true,
        message: error500Text
      }))
      return rejectWithValue(err);
    }
  }
);

export const getGroupedAndSortedMasterTrainees = createAsyncThunk(
  "trainees/getGroupedAndSortedMasterTrainees",
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const res = await TraineeService.getGroupedAndSortedTrainees(
        data.group,
        data.sort,
        1
      );
      if (res.error) {
        dispatch(setError({
          isOpen: true,
          message: res.error
        }))
        return rejectWithValue(res.error);
      } else if (res.users) {
        return res.users;
      } else {
        dispatch(setError({
          isOpen: true,
          message: error500Text
        }))
        return rejectWithValue(error500Text);
      }
    } catch (err) {
      dispatch(setError({
        isOpen: true,
        message: error500Text
      }))
      return rejectWithValue(err);
    }
  }
);

export const getTrainees = createAsyncThunk(
  "trainees/getTrainees",
  async (data: { forceReload?: boolean } | undefined, { rejectWithValue, dispatch }) => {
    try {
      const res = await TraineeService.getTrainees();
      if (res.error) {
        dispatch(setError({
          isOpen: true,
          message: res.error
        }))
        return rejectWithValue(res.error);
      } else if (res.users) {
        return res.users;
      } else {
        dispatch(setError({
          isOpen: true,
          message: error500Text
        }))
        return rejectWithValue(error500Text);
      }
    } catch (err) {
      dispatch(setError({
        isOpen: true,
        message: error500Text
      }))
      return rejectWithValue(err);
    }
  }, {
  condition: (data: { forceReload?: boolean } | undefined, { getState }) => {
    
    if (data && data.forceReload) {return true;}

    /* @ts-ignore */
    const { traineeList } = getState()
    if (traineeList.traineesWithOpenEntries.length > 0 || traineeList.loading === true) {
      // Already fetched or in progress, don't need to re-fetch
      return false
    }
  },
}
);

export const traineeListSlice = createSlice({
  name: "trainees",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    /* get master Trainees */
    builder.addCase(getGroupedAndSortedMasterTrainees.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(
      getGroupedAndSortedMasterTrainees.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.groupedAndSortedMasterTrainees = payload;
      }
    );
    builder.addCase(
      getGroupedAndSortedMasterTrainees.rejected,
      (state, { payload }) => {
        state.loading = false;
        /* @ts-ignore */
        state.error = payload;
      }
    );

    /* get additional Trainees */
    builder.addCase(getGroupedAndSortedAdditionalTrainees.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(
      getGroupedAndSortedAdditionalTrainees.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.groupedAndSortedAdditionalTrainees = payload;
      }
    );
    builder.addCase(
      getGroupedAndSortedAdditionalTrainees.rejected,
      (state, { payload }) => {
        state.loading = false;
        /* @ts-ignore */
        state.error = payload;
      }
    );

    /* get Trainees with open Entries*/
    builder.addCase(getTrainees.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(getTrainees.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.traineesWithOpenEntries = payload;
    });
    builder.addCase(getTrainees.rejected, (state, { payload }) => {
      state.loading = false;
      /* @ts-ignore */
      state.error = payload;
    });
  },
});

export default traineeListSlice.reducer;
